import type { StrategySlideImages, StrategySlideMedia } from "@/types/strategy.type";

/**
 * Every strategy detail fetch presigns slide media again, so a poll or a focus
 * refetch hands back new URLs for unchanged media. Swapping them in reloads
 * every image and restarts every video. Reuse the first URLs seen for the same
 * media until they near expiry (the backend presigns for 1 hour).
 */
const REUSE_MS = 45 * 60_000;
const URL_FIELDS = new Set(["url", "thumbnailUrl", "videoUrl", "imageUrl"]);
const seen = new Map<string, { media: StrategySlideMedia; at: number }>();

/** Everything but the signed URLs: a changed title or source is new media. */
const identity = (media: StrategySlideMedia) =>
  JSON.stringify(media, (key, value: unknown) => (URL_FIELDS.has(key) ? undefined : value));

export function stableSlideImages(images: StrategySlideImages): StrategySlideImages {
  const now = Date.now();
  const stable: StrategySlideImages = {};
  for (const [slot, media] of Object.entries(images)) {
    const id = identity(media);
    const hit = seen.get(id);
    if (hit && now - hit.at < REUSE_MS) {
      stable[slot] = hit.media;
    } else {
      seen.set(id, { media, at: now });
      stable[slot] = media;
    }
  }
  return stable;
}

const seenPictures = new Map<string, { url: string; at: number }>();

/** The same, for the document's library pictures ("media:<id>" → its presigned link). */
export function stablePictureUrls(urls: Record<string, string>): Record<string, string> {
  const now = Date.now();
  const stable: Record<string, string> = {};
  for (const [ref, url] of Object.entries(urls)) {
    const hit = seenPictures.get(ref);
    if (hit && now - hit.at < REUSE_MS) {
      stable[ref] = hit.url;
    } else {
      seenPictures.set(ref, { url, at: now });
      stable[ref] = url;
    }
  }
  return stable;
}
